import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from "react-i18next";
import { useFormik } from "formik";
import * as yup from 'yup';
import { FormField } from "@jpmuitk/form-field";
import { Input } from "@jpmuitk/input";
import { Euro, AttachMoney } from "@material-ui/icons";
import { Card } from "@jpmuitk/card";
import { Button } from "@jpmuitk/button";
import { Spinner } from "@jpmuitk/spinner";
import { ColumnLayout } from '@jpmuitk/column-layout';
import {
  initValidationFallback,
  verifyMicrodeposits,
  notifyMicrodepositLoaded,
} from '../../redux/clientActions';
import ConfirmationScreen from "./ConfirmationScreen";
import { LinearProgress } from "@jpmuitk/progress";
import "./MicrodepositVerificationForm.css";
import { ADA_COMPLIANT_DARK_GRAY, ADA_COMPLIANT_RED } from '../ThemeConstants';
import { MICRODEPOSITS } from '../ProviderConstants';
import { FLOW_WINDOW_SECONDS, resolveRemainingSeconds } from './expiryUtils';

export default function MicrodepositVerificationForm({ accessToken, sessionToken, maskedAccount, successRedirectUrl,
  failureRedirectUrl, handleSetIsRedirecting, navigate, effectiveExpiresAt, accountRegion }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const expiryTargetRef = useRef(effectiveExpiresAt ?? Date.now() + FLOW_WINDOW_SECONDS * 1000);
  const initialRemaining = resolveRemainingSeconds(expiryTargetRef.current) ?? FLOW_WINDOW_SECONDS;
  const [timeLeft, setTimeLeft] = useState(initialRemaining);
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [terminalOutcome, setTerminalOutcome] = useState(null);
  const [endOfAccount] = useState(maskedAccount);
  const isRedirectingRef = useRef(false);
  const LinearShowNoInfo = () => <LinearProgress showInfo={false} value={formatCountdown(timeLeft)} size={"medium"} />;
  const initialValues = {
    firstDeposit: '',
    secondDeposit: ''
  }

  // Always measure progress against the full flow window so the bar reflects real elapsed time and
  // does not restart when the page is refreshed part-way through the countdown.
  const formatCountdown = (timeLeft) => {
    return ((FLOW_WINDOW_SECONDS - timeLeft) / FLOW_WINDOW_SECONDS) * 100;
  }

  const expireValidation = async () => {
    await initValidationFallback(accessToken, sessionToken, 10068306, "Expired", MICRODEPOSITS);
  };

  const cancelValidation = async () => {
    await initValidationFallback(accessToken, sessionToken, 8606, "Customer Canceled", MICRODEPOSITS);
  };

  const isActiveRef = useRef(true);
  const timerIdRef = useRef(null);

  const CurrencyIcon = accountRegion === "US" ? AttachMoney : Euro

  // Confirm to the backend that the microdeposit page has rendered so the session — deferred on AVS
  // while the page was switching — is promoted to MICRODEPOSIT (which starts the inactivity timer).
  // Until this fires the cache stays on AVS, so closing the tab before the form paints leaves an AVS
  // session that can cleanly re-enter (AVS-11068). Fire-and-forget: a failure only means the timer
  // starts on the next server touch, so it must never block or surface an error in the form.
  useEffect(() => {
    notifyMicrodepositLoaded(accessToken, sessionToken)
      .then((promoted) => {
        // The promotion resets the session-start timestamp, so its effectiveExpiresAt is the
        // authoritative rolling-window instant (e.g. 10 min). Adopt it here to correct a stale
        // token-cap value (e.g. 30 min) carried in from the deferred AVS poll response.
        if (promoted != null && promoted.effectiveExpiresAt != null) {
          expiryTargetRef.current = promoted.effectiveExpiresAt;
          setTimeLeft(resolveRemainingSeconds(promoted.effectiveExpiresAt));
        }
      })
      .catch((error) => {
        console.error('Failed to send microdeposit loaded signal', error);
      });
  }, []);

  // Keep the countdown target in sync with the server-authoritative expiry once it arrives (or
  // changes) so the timer always resolves against the canonical instant.
  useEffect(() => {
    if (effectiveExpiresAt != null) {
      expiryTargetRef.current = effectiveExpiresAt;
      setTimeLeft(resolveRemainingSeconds(effectiveExpiresAt));
    }
  }, [effectiveExpiresAt]);

  useEffect(() => {
    isActiveRef.current = true;

    // Derive the remaining time from the wall clock each tick rather than decrementing a counter, so
    // the countdown stays accurate even when the browser throttles or freezes background-tab timers.
    timerIdRef.current = setInterval(() => {
      if (!isActiveRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
        return;
      }
      setTimeLeft(resolveRemainingSeconds(expiryTargetRef.current) ?? 0);
    }, 1000);

    return () => {
      isActiveRef.current = false;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []);

  // When the tab regains focus after being hidden (and its timer throttled/paused), immediately
  // recompute the remaining time so an expiry that elapsed while backgrounded fires without waiting
  // for the next throttled tick.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActiveRef.current) {
        setTimeLeft(resolveRemainingSeconds(expiryTargetRef.current) ?? 0);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (showConfirmationScreen) {
      console.log('[MicrodepositVerificationForm] Confirmation screen shown, stopping timer');
      isActiveRef.current = false;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
      return;
    }

    if (timeLeft <= 0 && isActiveRef.current) {
      console.log('[MicrodepositVerificationForm] Time expired, stopping timer and redirecting');
      isActiveRef.current = false;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
      handleSetIsRedirecting(true);
      expireValidation().then();
      setTerminalOutcome("SUCCESS");
      setRedirectUrl(successRedirectUrl);
      setShowConfirmationScreen(true);
    }
  }, [timeLeft, showConfirmationScreen]);

  const handleCancel = async () => {
    const navigateTo = navigate ?? ((url) => window.location.replace(url));

    if (isCancelling) return;
    setIsCancelling(true);

    //bypass confirmation window
    isRedirectingRef.current = true;

    handleSetIsRedirecting?.(true);

    try {
      // await to ensure single server-side call before navigating
      await cancelValidation();
    } finally {
      // stop the timer only when we know we are leaving
      isActiveRef.current = false;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
      // navigate away; we intentionally do not reset isCancelling because we leave the page
      navigateTo(successRedirectUrl);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} ${t('minutes')} : ${remainingSeconds < 10 ? '0' : ''}${remainingSeconds} ${t('seconds')}`;
  };

  const microdepositVerificationForm = useFormik({
    initialValues: initialValues,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setLoading(true);
      try {
        const res = await verifyMicrodeposits(accessToken, sessionToken, values.firstDeposit, values.secondDeposit);
        const component = res[0].component;
        if (component === "SUCCESS") {
          console.log('[MicrodepositVerificationForm] Verification succeeded, stopping timer');
          isActiveRef.current = false;
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          handleSetIsRedirecting(true);
          setTerminalOutcome("SUCCESS");
          setRedirectUrl(successRedirectUrl);
          setShowConfirmationScreen(true);
        } else if (component === "MICRODEPOSIT") {
          setAlertMessage(t('incorrect_amounts'));
          resetForm();
        } else if (component === "AWAITING_REDIRECT") {
          console.log('[MicrodepositVerificationForm] Verification reached awaiting-redirect, stopping timer');
          isActiveRef.current = false;
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          const outcome = res[0].pendingOutcome === "SUCCESS" ? "SUCCESS" : "FAILURE";
          handleSetIsRedirecting(true);
          setTerminalOutcome(outcome);
          setRedirectUrl(outcome === "SUCCESS" ? successRedirectUrl : failureRedirectUrl);
          setShowConfirmationScreen(true);
        } else {
          console.log('[MicrodepositVerificationForm] Verification failed terminally, stopping timer');
          isActiveRef.current = false;
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          handleSetIsRedirecting(true);
          setTerminalOutcome("FAILURE");
          setRedirectUrl(failureRedirectUrl);
          setShowConfirmationScreen(true);
        }
      } catch (error) {
        console.log('[MicrodepositVerificationForm] Verification error, stopping timer');
        isActiveRef.current = false;
        if (timerIdRef.current) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
        }
        handleSetIsRedirecting(true);
        setTerminalOutcome("FAILURE");
        setRedirectUrl(failureRedirectUrl);
        setShowConfirmationScreen(true);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
    validationSchema: yup.object().shape({
      firstDeposit: yup.number()
        .required(t('required_validation'))
        .min(0.01, t('min_validation'))
        .max(0.99, t('max_validation')).test(
          'is-decimal',
          t('decimal_required'),
          (value) => /^\d+(\.\d{1,2})?$/.test(value)
        ),
      secondDeposit: yup.number()
        .required(t('required_validation'))
        .min(0.01, t('min_validation'))
        .max(0.99, t('max_validation')).test(
          'is-decimal',
          t('decimal_required'),
          (value) => /^\d+(\.\d{1,2})?$/.test(value)
        ),
    })
  })

  if (showConfirmationScreen) {
    return <ConfirmationScreen redirectUrl={redirectUrl} setIsRedirecting={handleSetIsRedirecting} outcome={terminalOutcome} accessToken={accessToken} sessionToken={sessionToken} />;
  }

  return (
    <Card style={{ width: '100%', maxWidth: 850, minHeight: 450, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <ColumnLayout container={true} spacing={2}>
        <ColumnLayout item xs={12}>
          <h2 className="your-style-class">
            <Trans i18nKey="credited_deposits" values={{ accountNumber: endOfAccount }} />
          </h2>
        </ColumnLayout>
        <ColumnLayout item xs={12}>
          <form onSubmit={microdepositVerificationForm.handleSubmit}>
            <ColumnLayout container={true} spacing={2}>
              <ColumnLayout item xs={12} md={6}>
                <FormField
                  label={<span style={{ color: ADA_COMPLIANT_DARK_GRAY }}>{t('first_deposit')}</span>}
                  validationState={microdepositVerificationForm.errors.firstDeposit ? 'error' : ''}
                  helperText={microdepositVerificationForm.errors.firstDeposit
                    ? microdepositVerificationForm.errors.firstDeposit : ''}
                >
                  <Input
                    inputProps={{
                      step: "0.01"
                    }}
                    type={'number'}
                    placeholder="ex. 0.92"
                    value={microdepositVerificationForm.values.firstDeposit}
                    name={"firstDeposit"}
                    id={"firstDeposit"}
                    onChange={microdepositVerificationForm.handleChange}
                    onBlur={microdepositVerificationForm.handleBlur}
                    endAdornment={<CurrencyIcon style={{ paddingRight: "5px" }} />}
                  />
                </FormField>
              </ColumnLayout>
              <ColumnLayout item={true} xs={12} md={6}>
                <FormField
                  label={<span style={{ color: ADA_COMPLIANT_DARK_GRAY }}>{t('second_deposit')}</span>}
                  validationState={microdepositVerificationForm.errors.secondDeposit ? 'error' : ''}
                  helperText={microdepositVerificationForm.errors.secondDeposit
                    ? microdepositVerificationForm.errors.secondDeposit : ''}
                >
                  <Input
                    inputProps={{
                      step: "0.01"
                    }}
                    type={'number'}
                    placeholder="ex. 0.92"
                    value={microdepositVerificationForm.values.secondDeposit}
                    name={"secondDeposit"}
                    id={"secondDeposit"}
                    onChange={microdepositVerificationForm.handleChange}
                    onBlur={microdepositVerificationForm.handleBlur}
                    endAdornment={<CurrencyIcon style={{ paddingRight: "5px" }} />}
                  />
                </FormField>
              </ColumnLayout>
              <ColumnLayout item xs={12}>
                {alertMessage && <div style={{ color: ADA_COMPLIANT_RED }}>{alertMessage}</div>}
              </ColumnLayout>
              <ColumnLayout item={true} xs={12} md={'auto'}>
                <Button
                  className="submit-button"
                  type={'submit'}
                  variant={'cta'}
                  disabled={!microdepositVerificationForm.isValid || !microdepositVerificationForm.dirty || loading}>
                  {loading && <Spinner size={'small'} style={{ marginRight: 2 }} />}
                  {loading ? t('submitting') : t('submit')}
                </Button>
              </ColumnLayout>
              <ColumnLayout item={true} xs={12} md={'auto'}>
                <Button
                  data-testid="cancel-btn"
                  className="submit-button"
                  type={'button'}
                  variant={'secondary'}
                  onClick={handleCancel}
                  disabled={isCancelling || loading || showConfirmationScreen}>
                  {isCancelling ? t('cancelling') : t('click_to_cancel')}
                </Button>
              </ColumnLayout>
            </ColumnLayout>
          </form>
        </ColumnLayout>
        <ColumnLayout item xs={12}>
          <div style={{
            marginTop: '20px'
          }} className="linear-progress">
            <LinearShowNoInfo />
          </div>
          <div style={{
            display: 'flex', flexDirection: 'row',
            justifyContent: 'center', alignItems: 'center', width: '100%'
          }}>
            {t('timeout_warning')}: <p
              style={{ fontWeight: 700, marginLeft: '5px' }}>{formatTime(timeLeft)}.</p>
          </div>
        </ColumnLayout>
      </ColumnLayout>
    </Card>
  )
}
